import React, { useEffect, useRef, useState } from "react";
import { MatrixHero } from "../../common/components/MatrixHero";
import "../../scss/tactical_panel.scss";
import { useI18n } from "../../i18n/I18nProvider";
import type { TranslationKey } from "../../i18n/translations";

/* ═══════════════ TOOL DATA ═══════════════ */

interface ToolMeta {
    id: string;
    titleKey: TranslationKey;
    descKey: TranslationKey;
    ctaKey: TranslationKey;
    href: string;
    external: boolean;
    color: string;
    colorRgb: string;
    icon: React.ReactNode;
}

const ShieldIcon = () => (
    <svg
        width="40"
        height="40"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
    >
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    </svg>
);

const GearIcon = () => (
    <svg
        width="40"
        height="40"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
    >
        <circle cx="12" cy="12" r="3" />
        <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
    </svg>
);

const UserSearchIcon = () => (
    <svg
        width="40"
        height="40"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
    >
        <circle cx="10" cy="8" r="5" />
        <path d="M2 21a8 8 0 0 1 16 0" />
        <circle cx="18" cy="14" r="3" />
        <path d="m21 17-1.5-1.5" />
    </svg>
);

const DatabaseIcon = () => (
    <svg
        width="40"
        height="40"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
    >
        <ellipse cx="12" cy="5" rx="9" ry="3" />
        <path d="M21 12c0 1.66-4.03 3-9 3s-9-1.34-9-3" />
        <path d="M3 5v14c0 1.66 4.03 3 9 3s9-1.34 9-3V5" />
    </svg>
);

const ClockRewindIcon = () => (
    <svg
        width="40"
        height="40"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
    >
        <circle cx="12" cy="12" r="10" />
        <polyline points="12 6 12 12 16 14" />
        <path d="M4.93 4.93l2.12 2.12" />
        <path d="M2 12h3" />
    </svg>
);

const LockIcon = () => (
    <svg
        width="40"
        height="40"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
    >
        <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
        <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </svg>
);

const TOOLS: ToolMeta[] = [
    {
        id: "gen",
        titleKey: "tactical_tool_generator_title",
        descKey: "tactical_tool_generator_desc",
        ctaKey: "tactical_tool_generator_cta",
        href: "https://bitwarden.com/password-generator/",
        external: true,
        color: "#0ea5e9",
        colorRgb: "14,165,233",
        icon: <GearIcon />,
    },
    {
        id: "str",
        titleKey: "tactical_tool_strength_title",
        descKey: "tactical_tool_strength_desc",
        ctaKey: "tactical_tool_strength_cta",
        href: "https://pl.vpnmentor.com/tools/passwordmeter/",
        external: false,
        color: "#14b8a6",
        colorRgb: "20,184,166",
        icon: <ShieldIcon />,
    },
    {
        id: "login",
        titleKey: "tactical_tool_email_title",
        descKey: "tactical_tool_email_desc",
        ctaKey: "tactical_tool_email_cta",
        href: "https://whatsmyname.me/",
        external: true,
        color: "#06b6d4",
        colorRgb: "6,182,212",
        icon: <UserSearchIcon />,
    },
    {
        id: "pass",
        titleKey: "tactical_tool_password_title",
        descKey: "tactical_tool_password_desc",
        ctaKey: "tactical_tool_password_cta",
        href: "https://haveibeenpwned.com/Passwords",
        external: true,
        color: "#ef4444",
        colorRgb: "239,68,68",
        icon: <LockIcon />,
    },
    {
        id: "osint",
        titleKey: "tactical_tool_osint_title",
        descKey: "tactical_tool_osint_desc",
        ctaKey: "tactical_tool_osint_cta",
        href: "https://intelx.io/",
        external: true,
        color: "#a855f7",
        colorRgb: "168,85,247",
        icon: <DatabaseIcon />,
    },
    {
        id: "archive",
        titleKey: "tactical_tool_archive_title",
        descKey: "tactical_tool_archive_desc",
        ctaKey: "tactical_tool_archive_cta",
        href: "https://web.archive.org/",
        external: true,
        color: "#f59e0b",
        colorRgb: "245,158,11",
        icon: <ClockRewindIcon />,
    },
];

/* ═══════════════ CARD VISUALS ═══════════════ */

const CardVisual: React.FC<{ id: string }> = ({ id }) => {
    const { t } = useI18n();
    switch (id) {
        case "gen":
            return (
                <div className="tact-vis tact-vis--keygen" aria-hidden="true">
                    <svg className="tact-vis__gear-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="3" />
                        <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
                    </svg>
                    <div className="tact-vis__pw-list">
                        <span className="tact-vis__pw">xK$9mQp2</span>
                        <span className="tact-vis__pw">Fj#7rLw4</span>
                        <span className="tact-vis__pw">Z&amp;3nBvT8</span>
                        <span className="tact-vis__pw">aP*5dYc1</span>
                    </div>
                </div>
            );
        case "str":
            return (
                <div className="tact-vis tact-vis--strength" aria-hidden="true">
                    {[4, 6, 8, 12, 16, 20, 14, 10].map((n, i) => (
                        <div key={i} className="tact-vis__col-wrap" style={{ animationDelay: `${i * 0.25}s` } as React.CSSProperties}>
                            <div
                                className="tact-vis__col"
                                style={{ "--h": n / 20 } as React.CSSProperties}
                            />
                            <span className="tact-vis__col-num">{n}</span>
                        </div>
                    ))}
                </div>
            );
        case "login":
            return (
                <div className="tact-vis tact-vis--scanner" aria-hidden="true">
                    <div className="tact-vis__search-bar">
                        <svg className="tact-vis__search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="11" cy="11" r="8" />
                            <path d="M21 21l-4.35-4.35" />
                        </svg>
                        <span className="tact-vis__search-text">username</span>
                        <span className="tact-vis__search-cursor" />
                    </div>
                    <div className="tact-vis__results">
                        {[t("tactical_vis_no_results"), t("tactical_vis_checking"), t("tactical_vis_leaks_found")].map((text, i) => (
                            <span key={i} className="tact-vis__result" style={{ animationDelay: `${i * 2.5}s` } as React.CSSProperties}>{text}</span>
                        ))}
                    </div>
                </div>
            );
        case "osint":
            return (
                <div className="tact-vis tact-vis--osint" aria-hidden="true">
                    <div className="tact-vis__db-stack">
                        <span className="tact-vis__db-row tact-vis__db-row--1">PHONEBOOK</span>
                        <span className="tact-vis__db-row tact-vis__db-row--2">LEAKS</span>
                        <span className="tact-vis__db-row tact-vis__db-row--3">DARKNET</span>
                        <span className="tact-vis__db-row tact-vis__db-row--4">WHOIS</span>
                        <span className="tact-vis__db-row tact-vis__db-row--5">DNS</span>
                    </div>
                </div>
            );
        case "archive":
            return (
                <div className="tact-vis tact-vis--archive" aria-hidden="true">
                    <div className="tact-vis__timeline">
                        {["2005", "2010", "2015", "2020", "2025"].map((yr, i) => (
                            <span key={yr} className="tact-vis__year" style={{ animationDelay: `${i * 0.8}s` } as React.CSSProperties}>{yr}</span>
                        ))}
                    </div>
                    <div className="tact-vis__timeline-bar" />
                </div>
            );
        case "pass":
            return (
                <div className="tact-vis tact-vis--breach" aria-hidden="true">
                    <div className="tact-vis__lock">
                        <svg viewBox="0 0 24 24" strokeLinecap="round" strokeLinejoin="round">
                            <rect x="3" y="11" width="18" height="11" rx="2" />
                            <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                        </svg>
                    </div>
                    <span className="tact-vis__leak tact-vis__leak--1">p@ss123</span>
                    <span className="tact-vis__leak tact-vis__leak--2">qwerty</span>
                    <span className="tact-vis__leak tact-vis__leak--3">admin1</span>
                    <span className="tact-vis__leak tact-vis__leak--4">1234ab</span>
                    <span className="tact-vis__leak tact-vis__leak--5">hunter2</span>
                </div>
            );
        default:
            return null;
    }
};

/* ═══════════════ useInView ═══════════════ */

function useInView(threshold = 0.12) {
    const ref = useRef<HTMLDivElement>(null);
    const [inView, setInView] = useState(false);
    useEffect(() => {
        const obs = new IntersectionObserver(
            ([e]) => {
                if (e.isIntersecting) {
                    setInView(true);
                    obs.disconnect();
                }
            },
            { threshold }
        );
        if (ref.current) obs.observe(ref.current);
        return () => obs.disconnect();
    }, [threshold]);
    return { ref, inView };
}

/* ═══════════════ PAGE ═══════════════ */

const TacticalPanelPage: React.FC = () => {
    const intro = useInView();
    const grid = useInView(0.08);
    const { t } = useI18n();

    return (
        <div className="tact-page">
            <MatrixHero
                title={t("tactical_hero_title")}
                subtitle={t("tactical_hero_subtitle")}
                density={0.45}
            />

            {/* INTRO */}
            <section className="tact-page__section tact-page__bg">
                <div
                    className={`tact-page__intro${
                        intro.inView ? " --visible" : ""
                    }`}
                    ref={intro.ref}
                >
                    <h2 className="tact-page__section-title">
                        {t("tactical_intro_heading")}
                    </h2>
                    <p className="tact-page__section-text">
                        {t("tactical_intro_text")}
                    </p>
                </div>
            </section>

            {/* TOOLS GRID */}
            <section className="tact-page__section tact-page__bg">
                <div
                    className={`tact-page__grid-wrap${
                        grid.inView ? " --visible" : ""
                    }`}
                    ref={grid.ref}
                >
                    <div className="tact-grid">
                        {TOOLS.map((tool, i) => (
                            <a
                                key={tool.id}
                                className="tact-card"
                                href={tool.href}
                                target={
                                    tool.external ? "_blank" : undefined
                                }
                                rel={
                                    tool.external
                                        ? "noopener noreferrer"
                                        : undefined
                                }
                                style={
                                    {
                                        "--card-color": tool.color,
                                        "--card-rgb": tool.colorRgb,
                                        "--i": i,
                                    } as React.CSSProperties
                                }
                                aria-label={`${t(tool.titleKey)} — ${t(tool.ctaKey)}`}
                            >
                                {/* corner brackets */}
                                <span
                                    className="tact-card__corner tact-card__corner--tl"
                                    aria-hidden="true"
                                />
                                <span
                                    className="tact-card__corner tact-card__corner--tr"
                                    aria-hidden="true"
                                />
                                <span
                                    className="tact-card__corner tact-card__corner--bl"
                                    aria-hidden="true"
                                />
                                <span
                                    className="tact-card__corner tact-card__corner--br"
                                    aria-hidden="true"
                                />

                                {/* orbiting border glow */}
                                <span
                                    className="tact-card__orbit"
                                    aria-hidden="true"
                                />

                                {/* card content */}
                                <div className="tact-card__inner">
                                    <div className="tact-card__top-row">
                                        <span className="tact-card__icon">
                                            {tool.icon}
                                        </span>
                                    </div>
                                    <h3 className="tact-card__title">
                                        {t(tool.titleKey)}
                                    </h3>
                                    <p className="tact-card__desc">
                                        {t(tool.descKey)}
                                    </p>
                                    <CardVisual id={tool.id} />
                                    <span className="tact-card__cta">
                                        [ {t(tool.ctaKey)} ]
                                    </span>
                                </div>
                            </a>
                        ))}
                    </div>
                </div>
            </section>
        </div>
    );
};

export default TacticalPanelPage;
